/**
 * @component group-invite-modal
 * @css group-invite-modal.css
 * @primitives .btn.btn-primary, .btn.btn-secondary
 */
import { escapeHtml } from '../utils/html-utils.js';

class GroupInviteModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.groupId = null;
    this.groupName = '';
    this.activeTab = 'friends';
    this.friends = [];
    this.selectedFriendId = null;
    this.loadingFriends = false;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  open({ groupId, groupName }) {
    this.groupId = groupId;
    this.groupName = groupName || '';
    this.isOpen = true;
    this.activeTab = 'friends';
    this.selectedFriendId = null;
    this.friends = [];
    this.render();
    document.body.style.overflow = 'hidden';
    this.loadFriends();
    this.attachEventListeners();
  }

  close() {
    this.isOpen = false;
    this.isSubmitting = false;
    this.render();
    document.body.style.overflow = '';
  }

  async loadFriends() {
    if (this.loadingFriends || !this.isOpen) return;
    this.loadingFriends = true;
    this.updateTabContent();
    try {
      const response = await window.API.request('/friends');
      this.friends = response.friends || [];
    } catch {
      this.friends = [];
    } finally {
      this.loadingFriends = false;
      this.updateTabContent();
    }
  }

  updateTabContent() {
    const content = this.querySelector('.invite-tab-content');
    if (content) {
      content.innerHTML = this.renderActiveTab();
      this.attachTabContentListeners();
    }
  }

  renderActiveTab() {
    if (this.activeTab === 'email') {
      return `
        <form id="emailInviteForm">
          <div class="form-group">
            <label for="inviteEmailInput">Email address</label>
            <input
              type="email"
              id="inviteEmailInput"
              name="invite_email"
              placeholder="friend@example.com"
              required
            />
          </div>
        </form>
      `;
    }

    if (this.loadingFriends) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading friends…</p>
        </div>
      `;
    }

    if (this.friends.length === 0) {
      return '<p class="empty-hint">No friends to invite — try email instead.</p>';
    }

    return `
      <div class="friend-pick-list">
        ${this.friends.map((f) => {
    const checked = this.selectedFriendId === f.id ? 'checked' : '';
    return `
            <div class="friend-pick-item">
              <label>
                <input type="radio" name="friendPick" class="friend-radio" value="${f.id}" ${checked} />
                <span>${escapeHtml(f.username)}</span>
              </label>
            </div>
          `;
  }).join('')}
      </div>
    `;
  }

  render() {
    this.innerHTML = `
      <div class="group-invite-modal ${this.isOpen ? 'open' : ''}">
        <div class="modal-backdrop" id="modalBackdrop"></div>
        <div class="modal-container">
          <div class="modal-header">
            <h3>🤝 Invite to ${escapeHtml(this.groupName || 'group')}</h3>
            <button type="button" class="close-btn" id="closeBtn" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            <p class="modal-description">Invite a friend or send an email invitation to join this group.</p>
            <div class="invite-tabs">
              <button type="button" class="invite-tab ${this.activeTab === 'friends' ? 'active' : ''}" data-tab="friends">👥 Friend</button>
              <button type="button" class="invite-tab ${this.activeTab === 'email' ? 'active' : ''}" data-tab="email">📧 Email</button>
            </div>
            <div class="invite-tab-content">
              ${this.isOpen ? this.renderActiveTab() : ''}
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
              <button type="button" class="btn btn-primary" id="sendInviteBtn" ${this.isSubmitting ? 'disabled' : ''}>
                ${this.isSubmitting ? 'Sending…' : 'Send invite'}
              </button>
            </div>
            <div class="alert-container"></div>
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    if (!this.isOpen) return;

    this.querySelector('#modalBackdrop')?.addEventListener('click', () => this.close());
    this.querySelector('#closeBtn')?.addEventListener('click', () => this.close());
    this.querySelector('#cancelBtn')?.addEventListener('click', () => this.close());
    this.querySelector('#sendInviteBtn')?.addEventListener('click', () => this.handleSubmit());

    this.querySelectorAll('.invite-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activeTab = tab.getAttribute('data-tab');
        this.render();
        this.attachEventListeners();
        if (this.activeTab === 'friends' && this.friends.length === 0) {
          this.loadFriends();
        }
      });
    });

    this.attachTabContentListeners();
  }

  attachTabContentListeners() {
    this.querySelectorAll('.friend-radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.selectedFriendId = parseInt(radio.value, 10);
      });
    });
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    if (!container) return;
    container.innerHTML = `<div class="alert alert-${type}">${escapeHtml(message)}</div>`;
  }

  async handleSubmit() {
    if (this.isSubmitting || !this.groupId) return;
    this.isSubmitting = true;
    const sendBtn = this.querySelector('#sendInviteBtn');
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.textContent = 'Sending…';
    }

    try {
      let body = {};
      if (this.activeTab === 'friends') {
        if (!this.selectedFriendId) {
          this.showAlert('Pick a friend to invite', 'error');
          return;
        }
        body = { invited_user_id: this.selectedFriendId };
      } else {
        const email = this.querySelector('#inviteEmailInput')?.value?.trim();
        if (!email) {
          this.showAlert('Enter an email address', 'error');
          return;
        }
        body = { invite_email: email };
      }

      await window.API.request(`/groups/${this.groupId}/invites`, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      this.dispatchEvent(new CustomEvent('group-invite-sent', { bubbles: true }));
      this.close();
    } catch (error) {
      this.showAlert(error.message || 'Could not send invite', 'error');
    } finally {
      this.isSubmitting = false;
      const btn = this.querySelector('#sendInviteBtn');
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Send invite';
      }
    }
  }
}

customElements.define('group-invite-modal', GroupInviteModal);
export default GroupInviteModal;
